import { useState, useEffect } from 'react';
import Logo from './Logo';
import { Menu } from 'lucide-react';
import { SignInButton, SignOutButton, UserButton, useUser, SignedOut, SignUpButton } from "@clerk/clerk-react";
import { useLocation, Link, useNavigate } from 'react-router-dom';
import PricingModal from './modals/PricingModal';
import MobileMenu from './MobileMenu';
import subscriptionCheck, { checkIsPro } from '../utils/subscription';

const NavBar = () => {
  const { isSignedIn } = useUser();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isProSubscriber, setIsProSubscriber] = useState(false);

  const isDashboard = location.pathname === '/dashboard';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Check subscription status when signed in
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (isSignedIn) {
        try {
          // Use the checkIsPro function which returns a boolean directly
          const isPro = await checkIsPro();
          setIsProSubscriber(isPro);
        } catch (error) {
          console.error('Error checking subscription status:', error);
          setIsProSubscriber(false);
        }
      }
    };

    fetchSubscriptionStatus();

    // Also check when URL contains payment=success
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment') === 'success') {
      setIsProSubscriber(true);
      // Clear the URL parameters
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [isSignedIn, location.pathname, location.search]);

// Navigation timing constants
const NAVIGATION_TIMEOUT_MS = 3000; // Maximum time to wait for navigation
const SCROLL_RETRY_INTERVAL_MS = 50; // How often to check for element availability

  const scrollToSection = (sectionId) => {
    // If we're not on the homepage, navigate to homepage first
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      
      // Use a more robust approach: poll for element availability with timeout
      const scrollToElementWhenReady = () => {
        const startTime = Date.now();
        
        const pollForElement = () => {
          const element = document.getElementById(sectionId);
          
          if (element) {
            // Element found, scroll to it
            element.scrollIntoView({ behavior: 'smooth' });
            return;
          }
          
          // Check if we've exceeded the timeout
          if (Date.now() - startTime > NAVIGATION_TIMEOUT_MS) {
            console.warn(`Navigation timeout: Could not find element with id "${sectionId}" after ${NAVIGATION_TIMEOUT_MS}ms`);
            return;
          }
          
          // Element not found yet, try again
          setTimeout(pollForElement, SCROLL_RETRY_INTERVAL_MS);
        };
        
        pollForElement();
      };
      
      scrollToElementWhenReady();
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn(`Element with id "${sectionId}" not found on current page`);
      }
    }
  };

  return (
    <div className="fixed w-full top-0 z-50">
      <nav className={`transition-all duration-300 ${
        scrolled 
          ? 'bg-white/20 backdrop-blur-xl border-b border-white/20' 
          : 'bg-transparent border-b border-transparent'
      }`}>
        <div className="px-6 h-16 flex items-center justify-between max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo className="h-8 w-auto" variant="dark" />
            {isProSubscriber && isSignedIn && (
              <span className="-ml-12 bg-indigo-600 text-white text-xs font-bold px-2 py-1 rounded">PRO</span>
            )}
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {!isSignedIn && (
              <>
                <button 
                  onClick={() => scrollToSection('features')} 
                  className="text-white hover:text-gray-200 transition-colors font-medium"
                >
                  Features
                </button>
                <button 
                  onClick={() => scrollToSection('pricing')} 
                  className="text-white hover:text-gray-200 transition-colors font-medium"
                >
                  Pricing
                </button>
              </>
            )}
          </div>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {!isSignedIn && (
              <>
                <SignInButton mode="modal">
                  <button className="bg-white/80 backdrop-blur-sm hover:bg-white/90 border border-white/60 text-gray-900 text-sm px-4 py-2 rounded-lg font-medium transition-all duration-300">
                    Log in
                  </button>
                </SignInButton>
                
                <SignUpButton mode="modal">
                  <button className="bg-[#7C3AED] text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-[#6D28D9] transition-colors">
                    Sign up
                  </button>
                </SignUpButton>
              </>
            )}
            {/* Desktop User Button */}
            {isSignedIn && (
              <UserButton afterSignOutUrl="/" />
            )}
          </div>

          {/* Mobile Section: Render EITHER MobileMenu (signed out) OR UserButton (signed in) */}
          <div className="block md:hidden"> {/* Container visible only on mobile */}
            {!isSignedIn ? (
              <MobileMenu scrolled={scrolled} scrollToSection={scrollToSection} /> // Pass scrollToSection function to MobileMenu
            ) : (
              <UserButton afterSignOutUrl="/" /> // Show Clerk UserButton when signed in
            )}
          </div>

        </div>
      </nav>

      {/* Pricing Modal */}
      {showPricingModal && (
        <PricingModal 
          onClose={() => setShowPricingModal(false)} 
        />
      )}
    </div>
  );
};

export default NavBar;
